import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'

export type MeetingExchangeResource = {
  _id: string
  firstname: string
  lastname: string
  picture?: string
}

export type MeetingExchange = {
  _id: string
  fromId: MeetingExchangeResource
  toId: MeetingExchangeResource
  fromMeetingId: any
  toMeetingId: any
  fromMeetingStart: Date
  fromMeetingEnd: Date
  toMeetingStart: Date
  toMeetingEnd: Date
  accepted: boolean | null
  isClosed: boolean
  isClosedBySender: boolean
  fromMeetingDeleted: boolean
  toMeetingDeleted: boolean
  comment?: string
  _created: Date
  _firmId: string
}

export type MeetingExchangePayload = {
  fromId: string
  officeId: string
  countryId: string
  fromMeetingId: string
  fromMeetingStart: Date
  fromMeetingEnd: Date
  toMeetingId: string
  toMeetingStart: Date
  toMeetingEnd: Date
  toId: string
  _firmId: string
}

// A slot the recruiter can swap their meeting with, and the colleague who holds it
export type MeetingExchangeCandidate = {
  _id: string
  start: Date
  end: Date
  recruiter: MeetingExchangeResource & {
    position: { _id: string, display: string } | null
  }
}

export type ExchangeGroup = MeetingExchange & {
  exchanges: MeetingExchange[]
}

const afterReceivingExchange = (data: any) => {
  if (!data) return data
  const transform = (exchange: any) => ({
    ...exchange,
    fromMeetingStart: exchange.fromMeetingStart ? new Date(exchange.fromMeetingStart) : exchange.fromMeetingStart,
    fromMeetingEnd: exchange.fromMeetingEnd ? new Date(exchange.fromMeetingEnd) : exchange.fromMeetingEnd,
    toMeetingStart: exchange.toMeetingStart ? new Date(exchange.toMeetingStart) : exchange.toMeetingStart,
    toMeetingEnd: exchange.toMeetingEnd ? new Date(exchange.toMeetingEnd) : exchange.toMeetingEnd,
    _created: exchange._created ? new Date(exchange._created) : exchange._created,
  })
  if (Array.isArray(data)) return data.map(transform)
  return transform(data)
}

const getByMeetingId = (axios: IFactoryFunctionRequest) => (meetingId: string) =>
  axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/meetingExchanges/v2/meeting/${meetingId}`,
    afterReceivingHook: afterReceivingExchange,
  })

// Job titles (`jobs`) and job categories (`jobCategories`) of the colleagues to keep
const getCandidates = (axios: IFactoryFunctionRequest) => (meetingId: string, query: { jobs?: string[], jobCategories?: string[] }) =>
  axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/meetingExchanges/v2/candidates/${meetingId}`,
    query,
    afterReceivingHook: (data: MeetingExchangeCandidate[]) => (Array.isArray(data)
      ? data.map(candidate => ({ ...candidate, start: new Date(candidate.start), end: new Date(candidate.end) }))
      : []),
  })

// The connected recruiter's job category, null when their job title has none
const getMyJobCategory = (axios: IFactoryFunctionRequest) => () =>
  axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/meetingExchanges/v2/myJobCategory`,
  })

const create = (axios: IFactoryFunctionRequest) => (payload: MeetingExchangePayload) =>
  axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/meetingExchanges/v2`,
    data: payload,
    afterReceivingHook: afterReceivingExchange,
  })

const getSentExchanges = (axios: IFactoryFunctionRequest) => () =>
  axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/meetingExchanges/v2/sent`,
    afterReceivingHook: afterReceivingExchange,
  })

const getReceivedExchanges = (axios: IFactoryFunctionRequest) => () =>
  axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/meetingExchanges/v2/received`,
    afterReceivingHook: afterReceivingExchange,
  })

const acceptExchange = (axios: IFactoryFunctionRequest) => (data: { exchangeId: string; fromMeetingProcess?: string | null; toMeetingProcess?: string | null }) =>
  axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/meetingExchanges/v2/acceptExchange`,
    data,
  })

const updateExchange = (axios: IFactoryFunctionRequest) => (id: string, data: { accepted: boolean; comment?: string }) =>
  axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/meetingExchanges/v2/${id}`,
    data,
  })

const closeExchange = (axios: IFactoryFunctionRequest) => (id: string) =>
  axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/meetingExchanges/v2/${id}`,
  })

const sendReminderExchange = (axios: IFactoryFunctionRequest) => (id: string) =>
  axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/meetingExchanges/v2/sendReminder/${id}`,
  })

const getAllExchanges = (axios: IFactoryFunctionRequest) => (firmId: string) =>
  axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/meetingExchanges/v2/all/${firmId}`,
    afterReceivingHook: afterReceivingExchange,
  })

export default (axios: AxiosInstance) => {
  const factory = useAxios(axios)
  return {
    getByMeetingId: getByMeetingId(factory),
    getCandidates: getCandidates(factory),
    getMyJobCategory: getMyJobCategory(factory),
    create: create(factory),
    getSentExchanges: getSentExchanges(factory),
    getReceivedExchanges: getReceivedExchanges(factory),
    acceptExchange: acceptExchange(factory),
    updateExchange: updateExchange(factory),
    closeExchange: closeExchange(factory),
    sendReminderExchange: sendReminderExchange(factory),
    getAllExchanges: getAllExchanges(factory),
  }
}
